import { VdsIcon, VdsTooltip } from '@volt/volt-extended-components-react';
import styles from './Field.module.scss';
import { PropsWithChildren, ReactElement } from 'react';
import classNames from 'classnames';

export interface FieldProps {
  uid: string;
  label: ReactElement | string;
  errorMessage?: string;
  helperText?: string;
  tooltipLabel?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
}

export function Field({
  uid,
  label,
  tooltipLabel,
  errorMessage,
  helperText,
  isDisabled = false,
  isRequired = false,
  children,
}: PropsWithChildren<FieldProps>): ReactElement {
  const hasError = Boolean(errorMessage);
  const hasHelperText = Boolean(helperText);
  // Show only one message at a time, prioritising error messages first.
  const fieldMessage = hasError ? errorMessage : hasHelperText ? helperText : undefined;

  return (
    <div className={styles.field}>
      <div className={styles.field__header}>
        <label
          htmlFor={uid}
          className={classNames(styles.field__label, {
            [styles['field__label--required']]: isRequired,
          })}
        >
          {label}
        </label>
        {tooltipLabel && (
          <VdsTooltip
            id={`${uid}-tooltip`}
            event="hover"
            max-width="250px"
            position="bottom"
            type="standard"
            text={tooltipLabel}
            className={styles.tooltipFix}
          >
            <VdsIcon
              slot="tooltip-trigger"
              svgIcon="information"
              size="18px"
              color="color-icon-medium"
            />
          </VdsTooltip>
        )}
      </div>

      {/* Render the input */}
      {children}

      {/* Todo: Check if the span needs to always be rendered to prevent display bouncing. */}
      {fieldMessage && (
        <span
          className={classNames(styles.field__message, {
            [styles['field__message--error']]: hasError && !isDisabled,
            [styles['field__message--helper']]: hasHelperText && !isDisabled && !hasError,
            [styles['field__message--disabled']]: isDisabled,
          })}
        >
          {fieldMessage}
        </span>
      )}
    </div>
  );
}

export default Field;
